import { useAuth0 as useAuth0$1 } from '@auth0/auth0-react';
export { Auth0Provider as AuthProvider, withAuthenticationRequired } from '@auth0/auth0-react';
import React, { useState, useEffect, createContext, useContext } from 'react';
import jwtDecode from 'jwt-decode';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var useIdToken = function () {
    var _a = useAuth0$1(), getIdTokenClaims = _a.getIdTokenClaims, error = _a.error, isLoading = _a.isLoading, isAuthenticated = _a.isAuthenticated;
    var _b = useState(undefined), idToken = _b[0], setIdToken = _b[1];
    useEffect(function () {
        if (isAuthenticated && !isLoading && !error) {
            getIdTokenClaims().then(function (token) { return setIdToken(token); }).catch(function (e) { return setIdToken(undefined); });
        }
    }, [getIdTokenClaims, error, isLoading, isAuthenticated]);
    return idToken;
};

var useAccessToken = function () {
    var _a = useAuth0$1(), getAccessTokenSilently = _a.getAccessTokenSilently, error = _a.error, isLoading = _a.isLoading, isAuthenticated = _a.isAuthenticated;
    var _b = useState(undefined), accessToken = _b[0], setAccessToken = _b[1];
    useEffect(function () {
        if (isAuthenticated && !isLoading && !error) {
            // console.log('Attempting to get Access token in hook as we are authenticated');
            getAccessTokenSilently().then(function (token) {
                // console.log('Setting access token', token);
                setAccessToken(token);
            }).catch(function (e) {
                setAccessToken(undefined);
                // console.log('Failed to set access token. Error:', e);
            });
        }
    }, [getAccessTokenSilently, error, isLoading, isAuthenticated]);
    return accessToken;
};

var getScopes = function (accessToken) {
    if (!accessToken)
        return [];
    var decodedAccessToken = jwtDecode(accessToken);
    var ignoredScopes = ['openid', 'profile', 'email'];
    var scopes = decodedAccessToken &&
        decodedAccessToken.scope &&
        decodedAccessToken.scope.split(' ').filter(function (scope) { return !ignoredScopes.includes(scope); });
    return scopes || [];
};
var useScopes = function () {
    var accessToken = useAccessToken();
    var _a = useState(undefined), scopes = _a[0], setScopes = _a[1];
    useEffect(function () {
        if (accessToken) {
            setScopes(getScopes(accessToken));
        }
    }, [accessToken]);
    return scopes;
};

var AuthDataContext = createContext({});
var useAuth0Data = function () { return useContext(AuthDataContext); };
var AuthDataProvider = function (_a) {
    var children = _a.children;
    var idToken = useIdToken();
    var accessToken = useAccessToken();
    var scopes = useScopes();
    if (!idToken || !accessToken || !scopes)
        return React.createElement(React.Fragment, null);
    return React.createElement(AuthDataContext.Provider, { value: {
            accessToken: accessToken,
            idToken: idToken,
            scopes: scopes,
        } }, children);
};

var preparePlaformInfo = function (accessToken, appInfo) {
    return {
        neonUserId: getNeonUserId(accessToken),
        appName: appInfo.appName,
        appVersion: appInfo.appVersion,
        sessionId: appInfo.sessionId,
        dateTime: new Date().toISOString(),
        data: getPlatformInfo()
    };
};
function getNeonUserId(accessToken) {
    var defaultId = 'undefined';
    if (!accessToken)
        return defaultId;
    var decodedToken = jwtDecode(accessToken);
    if (!decodedToken)
        return defaultId;
    var propId = Object.keys(decodedToken).find(function (x) { return x.includes("neonUserId"); });
    if (!propId)
        return defaultId;
    return decodedToken[propId];
}
function getPlatformInfo() {
    var bowser = require('bowser');
    var browser = bowser.getParser(window.navigator.userAgent);
    var cookies = window.navigator.cookieEnabled;
    var browserInfo = browser.parse();
    return {
        'cookies': cookies,
        'browser': browserInfo.parsedResult.browser.name,
        'browserVersion': browserInfo.parsedResult.browser.version,
        'os': browserInfo.parsedResult.os.name,
        'osVersion': browserInfo.parsedResult.os.version,
        'osName': browserInfo.parsedResult.os.versionName,
        'platform': browserInfo.parsedResult.platform.type
    };
}

var defaultScopeErrorMessage = 'User is not allowed to access this system';
function verifyScopes(requiredScopes, userScopes) {
    if (!requiredScopes)
        return true;
    var scopeList = requiredScopes.split(' ');
    if (!scopeList.some(function (x) { return userScopes.includes(x); })) {
        return false;
    }
    return true;
}
var AuthCheckLogin = function (_a) {
    var children = _a.children, loader = _a.loader, ErrorComponent = _a.errorComponentCallback, requiredScopes = _a.requiredScopes, invalidScopeMessage = _a.invalidScopeMessage, loginUri = _a.loginUri, redirectIfInvalidScopes = _a.redirectIfInvalidScopes, unAuthenticated = _a.unAuthenticated, appInfo = _a.appInfo, addLogEntry = _a.addLogEntry;
    var auth0Data = useAuth0$1();
    var scopes = useScopes();
    var accessToken = useAccessToken();
    var _b = useState(false), loginOk = _b[0], setLoginOk = _b[1];
    useEffect(function () {
        if (loginOk) {
            addLogEntry(preparePlaformInfo(accessToken, appInfo));
        }
    }, [loginOk]);
    if (auth0Data.isLoading) {
        return loader;
    }
    if (auth0Data.error)
        return ErrorComponent ? React.createElement(ErrorComponent, { user: auth0Data.user, error: auth0Data.error, message: auth0Data.error.message }) : React.createElement("p", null,
            "Error: ",
            auth0Data.error.message);
    if (!auth0Data.isAuthenticated) {
        if (unAuthenticated) {
            return unAuthenticated;
        }
        if (loginUri) {
            window.location.href = loginUri;
        }
        else {
            auth0Data.loginWithRedirect();
        }
        return React.createElement(React.Fragment, null);
    }
    if (!scopes) {
        return loader;
    }
    var scopesOK = verifyScopes(requiredScopes, scopes);
    if (!scopesOK) {
        if (redirectIfInvalidScopes) {
            window.location.href = redirectIfInvalidScopes;
            return React.createElement(React.Fragment, null);
        }
        return ErrorComponent ?
            React.createElement(ErrorComponent, { message: (invalidScopeMessage || defaultScopeErrorMessage), title: "No Access" }) :
            React.createElement("p", null,
                "Error: ",
                invalidScopeMessage || defaultScopeErrorMessage);
    }
    if (!loginOk) {
        setLoginOk(true);
    }
    return React.createElement(React.Fragment, null, children);
};

var useAuth0 = function () {
    var original = useAuth0$1();
    return __assign(__assign({}, original), { logout: function (options) {
            original.logout(__assign({ federated: true }, options));
        } });
};

export { AuthCheckLogin, AuthDataProvider, preparePlaformInfo, useAccessToken, useAuth0, useAuth0Data, useIdToken, useScopes };
